import effects from 'app/effects/effects'
import {
  HEADERS_IMPORT_CSV_FILE,
  PREFERRED_CHANNEL_OPTIONS,
  MSG_DUPLICATE_AUTOMATED_CALL_NUMBER,
  MSG_DUPLICATE_ENTRIES,
  MSG_DUPLICATE_UNIQUE_ID,
} from 'app/main/subscribersV4/AddSubscibers/components/newImport/constansts'

const requiredColumns = new Set(
  HEADERS_IMPORT_CSV_FILE.filter(({ required }) => required).flatMap(
    ({ header, errorKey }) => [header, errorKey],
  ),
)

export const generateSubscribersFieldErrors = (rows, failedSubscribers) => {
  let processedRows = rows.map((row) => {
    const failedSubscriber = failedSubscribers.find((subscriber) => {
      return subscriber.id === row.id
    })

    if (!failedSubscriber) {
      const { errors, ...rest } = row
      return rest
    }

    const { id, ...invalidFields } = failedSubscriber
    const hasRequiredFields = Object.keys(invalidFields).some((key) =>
      requiredColumns.has(key),
    )
    const isAutomatedRequired = isPreferredAutomatedCall(row, invalidFields)

    return {
      ...row,
      errors: {
        id,
        isRequiredFields: isAutomatedRequired || hasRequiredFields,
        invalidFields,
      },
    }
  })

  const firstSeenEmails = new Map()
  const firstSeenPhones = new Map()
  const firstSeenAutomatedCalls = new Map()
  const firstSeenUniqueIds = new Map()

  processedRows.forEach((row, index) => {
    if (row.email && !firstSeenEmails.has(row.email)) {
      firstSeenEmails.set(row.email, index)
    }

    if (row.country_code && row.phone_number) {
      const phoneKey = `${row.country_code}${row.phone_number}`
      if (!firstSeenPhones.has(phoneKey)) {
        firstSeenPhones.set(phoneKey, index)
      }
    }

    if (row.automated_call_country_code && row.automated_call_number) {
      const automatedCallKey = `${row.automated_call_country_code}${row.automated_call_number}`
      if (!firstSeenAutomatedCalls.has(automatedCallKey)) {
        firstSeenAutomatedCalls.set(automatedCallKey, index)
      }
    }

    if (row.unique_id && !firstSeenUniqueIds.has(row.unique_id)) {
      firstSeenUniqueIds.set(row.unique_id, index)
    }
  })

  return processedRows.map((row, index) => {
    const duplicateErrors = {}
    const errorFields = []

    if (
      row.email &&
      firstSeenEmails.has(row.email) &&
      firstSeenEmails.get(row.email) !== index
    ) {
      errorFields.push('email')
      duplicateErrors.emailOrPhone = MSG_DUPLICATE_ENTRIES
    }

    if (row.country_code && row.phone_number) {
      const phoneKey = `${row.country_code}${row.phone_number}`
      if (
        firstSeenPhones.has(phoneKey) &&
        firstSeenPhones.get(phoneKey) !== index
      ) {
        errorFields.push('phone_number')
        duplicateErrors.emailOrPhone = MSG_DUPLICATE_ENTRIES
      }
    }

    if (row.automated_call_country_code && row.automated_call_number) {
      const automatedCallKey = `${row.automated_call_country_code}${row.automated_call_number}`
      if (
        firstSeenAutomatedCalls.has(automatedCallKey) &&
        firstSeenAutomatedCalls.get(automatedCallKey) !== index
      ) {
        errorFields.push('automated_call_number')
        duplicateErrors.automated_call_number =
          MSG_DUPLICATE_AUTOMATED_CALL_NUMBER
      }
    }

    if (
      row.unique_id &&
      firstSeenUniqueIds.has(row.unique_id) &&
      firstSeenUniqueIds.get(row.unique_id) !== index
    ) {
      errorFields.push('unique_id')
      duplicateErrors.unique_id = MSG_DUPLICATE_UNIQUE_ID
    }

    if (Object.keys(duplicateErrors).length > 0) {
      if (!row.errors) {
        row.errors = {
          id: row.id,
          invalidFields: duplicateErrors,
          isRequiredFields: false,
          errorFields,
        }
      } else {
        row.errors.invalidFields = {
          ...row.errors.invalidFields,
          ...duplicateErrors,
          errorFields,
        }
      }

      const hasRequiredFields = Object.keys(row.errors.invalidFields).some(
        (key) => requiredColumns.has(key),
      )
      const isAutomatedRequired = isPreferredAutomatedCall(
        row,
        row.errors.invalidFields,
      )
      row.errors.isRequiredFields = hasRequiredFields || isAutomatedRequired
    }

    return row
  })
}

export const isPreferredAutomatedCall = (subscriber, invalidFields) =>
  subscriber?.preferred_contact_method?.toLowerCase() ===
    PREFERRED_CHANNEL_OPTIONS.AUTOMATED_CALL.toLowerCase() &&
  'automated_call_country_code' in invalidFields

export const generateFieldErrorsSingleSubscriber = (
  changedSubscriber,
  validateData,
) => {
  if (!Object.keys(validateData).length) {
    delete changedSubscriber.errors
  } else {
    const { id, ...invalidFields } = validateData
    const hasRequiredFields = Object.keys(invalidFields).some((key) =>
      requiredColumns.has(key),
    )
    const isAutomatedRequired = isPreferredAutomatedCall(
      changedSubscriber,
      invalidFields,
    )

    changedSubscriber.errors = {
      id,
      invalidFields,
      isRequiredFields: isAutomatedRequired || hasRequiredFields,
    }
  }
  return changedSubscriber
}

export const checkSubscribersImport = async ({ handleShowSnackbar }) => {
  try {
    const { isInProcess } =
      await effects.apis.importCSV.checkSubscribersImport()
    return isInProcess
  } catch (err) {
    handleShowSnackbar && handleShowSnackbar()
  }
}

export const mappedChangedHeaders = ({ rows = [], headers = [] }) => {
  const changedHeaderKeys = headers.filter(
    ({ changedHeaderkey }) => changedHeaderkey,
  )

  if (changedHeaderKeys.length === 0) return rows

  const headerKeys = new Set(
    headers.map(
      ({ headerKey, changedHeaderkey }) => changedHeaderkey || headerKey,
    ),
  )

  const headerMap = new Map(
    changedHeaderKeys.map(({ headerKey, changedHeaderkey }) => [
      headerKey,
      changedHeaderkey,
    ]),
  )

  return rows.map((row) => {
    const newRow = { id: row.id }
    let changedFields = {}
    for (const key in row) {
      const value = row[key]
      if (headerMap.has(key)) {
        changedFields[headerMap.get(key)] = value
      } else if (headerKeys.has(key)) {
        newRow[key] = value
      }
    }
    const newFields = new Map()
    changedFields = { ...newRow, ...changedFields }

    headers.forEach(({ headerKey, changedHeaderkey }) => {
      const fieldKey = changedHeaderkey || headerKey
      newFields.set(fieldKey, changedFields[fieldKey])
    })

    return Object.fromEntries(newFields)
  })
}

export const reverseMappedHeaders = ({
  rows = [],
  headers = [],
  oldRows = [],
}) => {
  const reverseHeaderMap = headers.reduce(
    (map, { headerKey, changedHeaderkey }) => {
      if (changedHeaderkey) {
        map.set(changedHeaderkey, headerKey)
      }
      return map
    },
    new Map(),
  )

  if (reverseHeaderMap.size === 0) return rows

  const originalHeaderKeys = new Set(headers.map(({ headerKey }) => headerKey))

  return rows.map((row, index) => {
    const newRow = { id: row.id || index }
    const restoredFields = {}

    for (const [key, value] of Object.entries(row)) {
      if (reverseHeaderMap.has(key)) {
        restoredFields[reverseHeaderMap.get(key)] = value
      } else if (originalHeaderKeys.has(key)) {
        newRow[key] = value
      }
    }
    const oldRow = oldRows[index] || {}
    return { ...oldRow, ...newRow, ...restoredFields }
  })
}

export const markDuplicatesByKeys = (
  rows,
  oldSubscriber,
  changedSubscriber,
  keys,
) => {
  const getFieldValue = (subscriber, key) => {
    if (key === 'phone_number') {
      return `${subscriber['country_code'] || ''}${subscriber[key] || ''}`
    }
    if (key === 'automated_call_number') {
      return `${subscriber['automated_call_country_code'] || ''}${subscriber[key] || ''}`
    }
    return subscriber[key] || ''
  }

  const getSerializedFieldValue = (value) => {
    return typeof value === 'object' ? JSON.stringify(value) : value
  }

  const createDuplicateFieldErrors = (checkFields, row, seen) => {
    const errorFields = []
    const errors = Object.entries(checkFields).reduce(
      (errors, [key, value]) => {
        const fieldValue = getFieldValue(row, key)
        const serializedFieldValue = getSerializedFieldValue(fieldValue)
        // Namespace the value with field type to prevent cross-field duplicates
        const namespacedValue = `${key}:${serializedFieldValue}`

        if (value && fieldValue && seen.has(namespacedValue)) {
          const errorMsg = getErrorMessageForDuplicateField(key)
          if (key === 'phone_number' || key === 'email') {
            errorFields.push(key)
            errors['emailOrPhone'] = errorMsg
          } else {
            errors[key] = errorMsg
          }
        } else if (value && fieldValue) {
          seen.add(namespacedValue)
        }

        return errors
      },
      {},
    )
    return { errors, errorFields }
  }

  const mergeErrors = (duplicateFieldErrors, existingErrors) => {
    return { ...duplicateFieldErrors, ...existingErrors }
  }

  const checkFields = keys.reduce((acc, key) => {
    acc[key] = getFieldValue(changedSubscriber, key)
    return acc
  }, {})

  function getOldFields(oldSubscriber, changedSubscriber) {
    const oldFields = {}

    for (const key in oldSubscriber) {
      if (oldSubscriber[key] !== changedSubscriber[key]) {
        oldFields[key] = oldSubscriber[key]
      }
    }

    return oldFields
  }

  const oldFields = getOldFields(oldSubscriber, changedSubscriber)

  const seen = new Set()
  const matchingRowIndex = rows.findIndex((row) => {
    const matchingKeys = Object.keys(row).filter(
      (key) => row[key] === oldFields[key],
    )
    return matchingKeys.length > 0
  })

  return rows.map((row, index) => {
    const { errors: duplicateFieldErrors, errorFields } =
      createDuplicateFieldErrors(checkFields, row, seen)
    const existingErrors = row.errors?.invalidFields || {}
    const mergedErrors = mergeErrors(duplicateFieldErrors, existingErrors)

    if (!Object.keys(mergedErrors).length) {
      const { errors, ...rest } = row
      return rest
    }

    if (matchingRowIndex === index) {
      const { errors, ...rest } = row

      const { invalidFields = {} } = errors || {}
      const newInvalidFields = { ...invalidFields }
      const isAutomatedRequired = isPreferredAutomatedCall(
        row,
        newInvalidFields,
      )

      if (Object.keys(newInvalidFields).length) {
        const hasRequiredFields = Object.keys(newInvalidFields).some((key) =>
          requiredColumns.has(key),
        )
        return {
          ...row,
          errors: {
            id: row.id,
            invalidFields: newInvalidFields,
            isRequiredFields: isAutomatedRequired || hasRequiredFields,
            errorFields,
          },
        }
      } else {
        return rest
      }
    }

    const hasRequiredFields = Object.keys(mergedErrors).some((key) =>
      requiredColumns.has(key),
    )

    const isAutomatedRequired = isPreferredAutomatedCall(row, mergedErrors)

    return {
      ...row,
      errors: {
        id: row.id,
        invalidFields: mergedErrors,
        isRequiredFields: isAutomatedRequired || hasRequiredFields,
        errorFields,
      },
    }
  })
}

const getErrorMessageForDuplicateField = (key) => {
  switch (key) {
    case 'unique_id':
      return MSG_DUPLICATE_UNIQUE_ID

    case 'email':
    case 'phone_number':
      return MSG_DUPLICATE_ENTRIES

    case 'automated_call_number':
      return MSG_DUPLICATE_AUTOMATED_CALL_NUMBER
  }
}

export const getTotalErrorCount = (rows) =>
  rows.filter(
    (row) =>
      row?.errors?.isRequiredFields &&
      Object.keys(row?.errors?.invalidFields || {}),
  )?.length || 0
